import { PreOrderButton, LOCATION_LIST_SELECTION_ALL } from './buttons/pre-order-button';
import AddToCart from './buttons/add-to-cart';
import Cart from './cart/cart';
import Mutation from './_mutation';
import { CartDiscounts, ProductPricesElements, ProductPrice } from './discounts';
import PreOrder from './pre-order';

const BUTTONS_REFRESH_INTERVAL = 300;

export default class Application {
  /**
   * @type {string}
   */
  #appName;

  /**
   * @type {AppConfig}
   */
  #config;

  /**
   * @type {AppLibrary}
   */
  #library;

  /**
   * @type {string}
   */
  #page;

  /**
   * @type {PreOrder}
   */
  #preOrder;

  /**
   * @type {PreOrderButton[]}
   */
  #preOrderButtons = [];

  /**
   * @type {ProductPrice[]}
   */
  #productPrices = [];

  /**
   * @param {AppConfig} config
   * @param {AppLibrary} library
   * @param {string} page
   */
  constructor (config, library, page) {
    this.#appName = config.env().appName;
    this.#config = config;
    this.#library = library;
    this.#page = page;
  }

  /**
   *
   */
  run () {
    const atcSelector = this.#config.getATCSelector(this.#page);
    const priceSelector = this.#config.getProductPriceSelector(this.#page);

    let cartDiscounts = new CartDiscounts(this.#appName, this.#config, this.#page);
    cartDiscounts.run();

    let shopConfig = this.#config.settings().config;
    if (shopConfig.locations && shopConfig.locations[0] !== LOCATION_LIST_SELECTION_ALL) {
      this.#library.getGeoLocation(geoData => {
        this.#preOrder = new PreOrder(this.#config, geoData);

        if (atcSelector) {
          this.#displayPreOrderButtons(atcSelector);
        }
        this.#displayProductPriceDiscounts(priceSelector);
        this.#setupCartListeners(geoData).then(
          ok => ok && this.#displayHiddenNativeElements()
        );
      });
    } else {
      this.#preOrder = new PreOrder(this.#config, null);

      if (atcSelector) {
        this.#displayPreOrderButtons(atcSelector);
      }
      this.#displayProductPriceDiscounts(priceSelector);
      this.#setupCartListeners(null).then(
        ok => ok && this.#displayHiddenNativeElements()
      );
    }
  }

  /**
   * @param {string} atcSelector
   */
  #displayPreOrderButtons (atcSelector) {
    let preOrderButtonsEnabled = false;
    let isAtcStyleCloned = false;

    let addToCart = new AddToCart(atcSelector, this.#page);
    addToCart.forEachButton(
      button => {
        if (!isAtcStyleCloned && PreOrderButton.isAtcStyleNotApplied(this.#config)) {
          isAtcStyleCloned = true;
          PreOrderButton.cloneStyle(atcSelector);
        }

        if (AddToCart.shouldSkip(button)) {
          return;
        }
        let preOrderButton = new PreOrderButton(
          button,
          this.#config,
          this.#library,
          this.#page,
          this.#preOrder
        );
        if (preOrderButtonsEnabled) {
          preOrderButton.checkAndDisplay();
        }
        this.#preOrderButtons.push(preOrderButton);
      }
    );

    let modificationAllowed = () => this.#page === 'products' || !window.Spurit.global.atc.buttons.isModifiedByOtherApps(this.#appName);

    let mutation = null;

    window.Spurit.global.atc.buttons.modify(
      this.#appName,
      () => {
        if (!modificationAllowed()) {
          return;
        }
        preOrderButtonsEnabled = true;
        this.#preOrderButtons.forEach(preOrderButton => preOrderButton.checkAndDisplay());

        if (!mutation) {
          mutation = new Mutation(
            () => {
              if (modificationAllowed()) {
                this.#preOrderButtons.reverse().forEach(
                  preOrderButton => preOrderButtonsEnabled && preOrderButton.checkAndDisplay()
                );
              } else {
                this.#preOrderButtons.forEach(
                  preOrderButton => preOrderButton.revertAllChanges()
                );
                if (mutation) {
                  mutation.stop();
                  mutation = null;
                }
              }
            },
            BUTTONS_REFRESH_INTERVAL
          );
        }

        /*this.createMutation(
          () => {
            if (modificationAllowed()) {
              this.#preOrderButtons.reverse().forEach(
                preOrderButton => preOrderButtonsEnabled && preOrderButton.checkAndDisplay()
              );
            } else {
              this.#preOrderButtons.forEach(
                preOrderButton => preOrderButton.revertAllChanges()
              );
              this.stopMutation();
            }
          },
          BUTTONS_REFRESH_INTERVAL,
        );*/
      },
      () => {
        preOrderButtonsEnabled = false;
        this.#preOrderButtons.forEach(preOrder => preOrder.revertAllChanges());
        if (mutation) {
          mutation.stop();
          mutation = null;
        }
        //this.stopMutation();
      },
      true
    );
  }

  /**
   * @param {string} priceSelector
   */
  #displayProductPriceDiscounts (priceSelector) {
    if (!this.#config.discountsEnabled) {
      return;
    }

    let prices = new ProductPricesElements(priceSelector);
    prices.forEachElement(
      element => {
        if (ProductPricesElements.shouldSkip(element)) {
          return;
        }
        let productPrice = new ProductPrice(
          element,
          this.#config,
          this.#preOrder
        );
        productPrice.checkAndDisplay();
        this.#productPrices.push(productPrice);
      }
    );

    new Mutation(
      () => {
        this.#productPrices.reverse().forEach(
          productPrice => productPrice.checkAndDisplay()
        );
      },
      BUTTONS_REFRESH_INTERVAL
    );
  }

  /**
   *
   */
  #displayHiddenNativeElements () {
    document.querySelector('body').classList.add('spo-loaded');
  }

  /**
   * Returns TRUE if cart-items are correct and page reloading is not needed
   * @param {{country_code:string}|null} geoData
   * @returns {Promise<boolean>}
   */
  async #setupCartListeners (geoData) {
    const cart = new Cart(this.#appName, this.#config, this.#library, geoData);
    const global = window.Spurit.global;
    let preventCartFixing = false;

    global.atc.onBeforeEventPrevented((event, target) => {
      return this.#preOrderButtons.some(
        preOrderButton => !!preOrderButton.buttonNew && (preOrderButton.buttonNew === target || preOrderButton.buttonNew.contains(target) || target.contains(preOrderButton.buttonNew))
      );
    });

    global.atc.onBefore((event, target, lineItems, submitMode, next) => {
      cart.fixLineItems(lineItems).then(lineItems => {
        console.log('Adding to cart:', JSON.stringify(lineItems));
        if (lineItems.length) {
          preventCartFixing = true;
          next(global.constants.ATC.SUBMIT_MODE_NATIVE, lineItems);
        } else {
          next(global.constants.ATC.SUBMIT_MODE_DENY, []);
        }
      });
    });
    global.atc.onAfter((event, target, lineItems, submitMode) => {
      if (submitMode !== global.constants.ATC.SUBMIT_MODE_NATIVE) {
        setTimeout(() => preventCartFixing = false, 1000);
      }
    });
    global.checkout.onCheckout((next) => {
      if (cart.fixCartBusy) {
        cart.onFixCartFinished(() => next(true));
      } else if (!preventCartFixing) {
        cart.fixCart().then(() => next(true));
      } else {
        next(true);
      }
    });
    cart.cartApi.onChange(() => {
      if (!preventCartFixing) {
        cart.fixCart().then(
          updated => updated && this.#page === 'cart' && document.location.reload()
        );
      }
    });
    let updated = await cart.fixCart();
    if (updated && this.#page === 'cart') {
      document.location.reload();
      return false;
    }
    return true;
  }
}